/**
This module provides a lightweight React context for handling translations within an application.
It includes a TranslationsProvider that supplies a translation function t() to components through the useTranslations() hook.

__count is (0 or 1) always use the __singular
e.g.

const translations = {
  
  "Hello, {user}": "Hello, {user}",
  "user__singular": "User",
  "user__plural": "Users",
  "You have added {number} of user__singular": "You have added {number} user",
  "You have added {number} of user__plural": "You have added {number} users",
  
}

t("user", { __count: 1 }) // → "User"
t("user", { __count: 0 }) // → "User"
t("user", { __count: 3 }) // → "Users"
t("Hello, {user}", { user: "John" }) // → "Hello, John"
t("You have added {number} of user", { number: 1 }) // → "You have added 1 user"
t("You have added {number} of user", { number: 3 }) // → "You have added 3 users"
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import fetchCustom from '../../wp_helpers/fetchCustom';
import getResponseJson from '../../wp_helpers/getResponseJson';
const TranslationsContext = createContext();

function TranslationsProvider({ children, translations = {}, urls = [] }) {
    const [localTranslations, setLocalTranslations] = useState(translations);

    const updateTranslations = useCallback((newTranslations) => {
        setLocalTranslations(prevTranslations => ({ ...prevTranslations, ...newTranslations }));
    }, []);

    // Pass the URLs to fetch translations
    useEffect(() => {
        for (const url of urls) {
            fetchCustom(url)
                .then(response => getResponseJson(response, { notification: false }))
                .then(data => {
                    updateTranslations(data || {});
                });
        }
    }, []);

    const t = useCallback((key, params = {}) => {
        let translationKey = key;

        // Handle pluralization with __count
        if (params.__count !== undefined) {
            // Only use plural if count is a positive number > 1 or Infinity
            const isPlural = typeof params.__count === 'number' &&
                !isNaN(params.__count) &&
                params.__count > 1;
            const pluralSuffix = isPlural ? '__plural' : '__singular';
            const pluralKey = `${key}${pluralSuffix}`;
            if (localTranslations[pluralKey]) {
                translationKey = pluralKey;
            }
        }

        let translation = localTranslations[translationKey] || translationKey;

        if (!localTranslations[translationKey]) {
            console.warn(`No translation found for: ${translationKey}`);
        }

        // Replace placeholders
        Object.entries(params).forEach(([paramKey, value]) => {
            if (paramKey !== '__count') {
                translation = translation?.replace(`{${paramKey}}`, value);
            }
        });

        return translation;
    }, [localTranslations]);

    return (
        <TranslationsContext.Provider value={{ t, updateTranslations, translations: localTranslations }}>
            {children}
        </TranslationsContext.Provider>
    );
}

const useTranslations = () => {
    const context = useContext(TranslationsContext);
    if (context === undefined) {
        throw new Error('useTranslations must be used within a TranslationsProvider');
    }
    return context;
};

// Ensure this is not tree-shaken
useTranslations.displayName = 'useTranslations';

// Named exports
export { TranslationsContext, TranslationsProvider, useTranslations };

// Default export
export default TranslationsProvider;